import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api-client';
import { DEFAULT_UX_COPY, type UxCopy } from '@/types/ux-copy';

export function useUxCopy() {
  const [uxCopy, setUxCopy] = useState<UxCopy>(DEFAULT_UX_COPY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // AbortController is available in browser environment
    const abortController = new globalThis.AbortController();
    let mounted = true;

    // Idiomatic React data-fetch effect — closure-bound `mounted` and
    // `abortController` make extraction add indirection without
    // reducing the cognitive load to read it.
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: see comment above
    const fetchUxCopy = async () => {
      try {
        const response = await apiFetch('/api/interview/ux', {
          signal: abortController.signal,
          cache: 'no-store',
        });

        if (!response.ok) {
          if (response.status === 401) {
            if (mounted) {
              setUxCopy(DEFAULT_UX_COPY);
            }
            return;
          }
          throw new Error(`Failed to fetch UX copy: ${response.status}`);
        }

        const data: { ux_copy: UxCopy } = await response.json();
        if (mounted) {
          setUxCopy(data.ux_copy);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('Error fetching UX copy:', err);
        if (mounted) {
          setError(err as Error);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchUxCopy();

    return () => {
      mounted = false;
      abortController.abort();
    };
  }, []);

  return { uxCopy, loading, error };
}
